import { useCallback } from 'react';
import { API } from '@gin-betting/core';

import { useToggler } from 'hooks';
import { CategorizerRowType } from '../../../constants';
import useCategorizerRowChecker from '../useCategorizerRowChecker';

interface Input {
    topCategorizerItem: API.CategorizerItem;
    items: API.CategorizerItem[];
    selectedTournamentIds: string[];
    isAllItemsInSportChecked: boolean;
    isGroupWithOneItem: boolean;
    onTournamentIdsChange: (nextTournamentIds: string[]) => void;
}

interface Output {
    activeTournamentIds: string[];
    isOpened: boolean;
    isSubgroupChecked: boolean;
    isTournamentCheckable: boolean;
    handleOpenToggle: VoidFn;
    handleSubgroupCheck: VoidFn;
    handleTournamentCheck: (checkedTournamentIds: string[]) => void;
}

const useCategorizerSubRow = ({
    items,
    topCategorizerItem,
    selectedTournamentIds,
    isAllItemsInSportChecked,
    isGroupWithOneItem,
    onTournamentIdsChange,
}: Input): Output => {
    const {
        itemTournamentIds,
        activeTournamentIds,
        isTournamentManuallyChecked,
        isSubgroupManuallyChecked,
        isAllTournamentsChecked,
        hasAnyCheckedTournament,
        handleItemCheck,
    } = useCategorizerRowChecker({
        items,
        topCategorizerItem,
        selectedTournamentIds,
        isAllItemsInSportChecked,
        isGroupWithOneItem,
        handleTournamentIdsChange: onTournamentIdsChange,
    });

    const isSubgroupChecked =
        (!isAllItemsInSportChecked ||
            isGroupWithOneItem ||
            isTournamentManuallyChecked ||
            isSubgroupManuallyChecked) &&
        hasAnyCheckedTournament;

    const [isOpened, handleOpenToggle] = useToggler(isSubgroupChecked);

    const isTournamentCheckable =
        !isAllTournamentsChecked ||
        isTournamentManuallyChecked ||
        ((isSubgroupManuallyChecked || isGroupWithOneItem) &&
            items.length === 1);

    const handleTournamentCheck = useCallback(
        (checkedTournamentIds: string[]) => {
            handleItemCheck(
                checkedTournamentIds,
                CategorizerRowType.Tournament
            );
        },
        [handleItemCheck]
    );

    const handleSubgroupCheck = useCallback(() => {
        handleItemCheck(itemTournamentIds, CategorizerRowType.Subgroup);
    }, [handleItemCheck, itemTournamentIds]);

    return {
        activeTournamentIds,
        isOpened,
        isTournamentCheckable,
        isSubgroupChecked,
        handleOpenToggle,
        handleSubgroupCheck,
        handleTournamentCheck,
    };
};

export default useCategorizerSubRow;
